/**
 * IppLauncher — Phase E configurable renderer
 *
 * Reads ipp_data from the org config loaded at partner-switch time
 * (state.orgConfigData.organization). If no config is found it renders
 * the `fallback` prop or a "not configured" message.
 *
 * Used as the default branch of any IPP-related switch in routes/components.js
 * so ALL existing partner cases continue to work unchanged.
 */
import React from 'react';
import { connect } from 'react-redux';
import { Box, Typography, Chip, CircularProgress, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { getPartner } from '../../lib/access.es6';

// Build the iframe src from ipp_data config + optional query params
function buildIframeSrc(ippData, partnerGuid) {
  var base = ippData.embed_url || '';
  if (!base) return '';
  var params = [];
  if (ippData.journey_code) {
    params.push('journey=' + encodeURIComponent(ippData.journey_code));
  }
  if (partnerGuid) {
    params.push('partner=' + encodeURIComponent(partnerGuid));
  }
  if (ippData.theme && ippData.theme.primary) {
    params.push('primary=' + encodeURIComponent(ippData.theme.primary));
  }
  if (ippData.theme && ippData.theme.secondary) {
    params.push('secondary=' + encodeURIComponent(ippData.theme.secondary));
  }
  // Extra query params stored as array of { key, value }
  if (ippData.params && Array.isArray(ippData.params)) {
    ippData.params.forEach(function(p) {
      if (p.key) { params.push(encodeURIComponent(p.key) + '=' + encodeURIComponent(p.value || '')); }
    });
  }
  return base + (params.length ? '?' + params.join('&') : '');
}

// Find the best matching IPP config for the current journey type
function resolveIppConfig(configs, journeyCode) {
  if (!configs || !configs.length) return null;
  // Prefer exact journey match, then first active config
  var match = null;
  var firstActive = null;
  for (var i = 0; i < configs.length; i++) {
    var c = configs[i];
    if (!c.active) continue;
    var ippData = c.ipp_data || {};
    if (!firstActive) firstActive = c;
    if (ippData.journey_code === journeyCode) { match = c; break; }
  }
  return match || firstActive;
}

class IppLauncher extends React.Component {
  constructor(props) {
    super(props);
    this.state = { iframeLoaded: false, iframeError: false };
    this.handleIframeLoad = this.handleIframeLoad.bind(this);
    this.handleIframeError = this.handleIframeError.bind(this);
  }

  handleIframeLoad() {
    this.setState({ iframeLoaded: true, iframeError: false });
  }

  handleIframeError() {
    this.setState({ iframeError: true });
  }

  render() {
    var orgData = this.props.orgConfigData || {};
    var org = orgData.organization || null;

    // Try to find IPP configs on the org object
    // They may be stored as org.ipp_configs (array) or a single ipp_data blob
    var ippConfigs = (org && org.ipp_configs) ? org.ipp_configs : [];
    var journeyCode = this.props.journeyCode || 'onboarding';
    var resolved = resolveIppConfig(ippConfigs, journeyCode);

    // If loading org config
    if (orgData.loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ color: '#FF8349' }} />
        </Box>
      );
    }

    // No config — show fallback if provided, or "not configured" message
    if (!resolved) {
      if (this.props.fallback) return this.props.fallback;
      var partner = getPartner();
      return (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', py: 10, gap: 2,
        }}>
          <LanguageOutlinedIcon sx={{ fontSize: 56, color: '#D1D5DB' }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>
            No IPP Configuration Found
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#6B7280', textAlign: 'center', maxWidth: 400 }}>
            No IPP Embeddable is configured for{' '}
            {partner ? partner.name : 'this partner'}.
            Set one up in the Organization → IPP Configs tab.
          </Typography>
        </Box>
      );
    }

    var ippData = resolved.ipp_data || {};
    var iframeSrc = buildIframeSrc(ippData, this.props.partnerGuid);
    var accentColor = (ippData.theme && ippData.theme.primary) ? ippData.theme.primary : '#FF8349';

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1,
          borderBottom: '1px solid #F3F4F6', bgcolor: '#FAFAFA',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageOutlinedIcon sx={{ fontSize: 18, color: accentColor }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              {ippData.label || resolved.code}
            </Typography>
            <Chip
              label={ippData.journey_code || 'onboarding'}
              size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: '#F5F3FF', color: '#6D28D9',
                border: '1px solid #DDD6FE',
              }}
            />
          </Box>
          {iframeSrc && (
            <Button
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              component="a"
              href={iframeSrc}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: 'none', fontSize: 12,
                color: accentColor,
              }}
            >
              Open in new tab
            </Button>
          )}
        </Box>

        {/* Spinner while iframe loads */}
        {!this.state.iframeLoaded && !this.state.iframeError && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            py: 6, position: 'absolute', width: '100%',
          }}>
            <CircularProgress size={36} sx={{ color: accentColor }} />
          </Box>
        )}

        {/* Iframe */}
        {iframeSrc && !this.state.iframeError && (
          <iframe
            src={iframeSrc}
            title={ippData.label || resolved.code}
            onLoad={this.handleIframeLoad}
            onError={this.handleIframeError}
            style={{
              flex: 1,
              border: 'none',
              width: '100%',
              minHeight: 600,
              opacity: this.state.iframeLoaded ? 1 : 0,
              transition: 'opacity 0.3s',
            }}
          />
        )}

        {this.state.iframeError && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: 14, color: '#B91C1C' }}>
              Failed to load IPP iframe. Check the embed URL configuration.
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 1, fontFamily: 'monospace' }}>
              {iframeSrc}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
}

export default connect(function(state) {
  return {
    orgConfigData: state.orgConfigData,
  };
})(IppLauncher);
